"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Global scroll manager (owner bug 2026-08-29: "the Save your day guide
 * always opens showing the END of the page"). BrowserRouter swaps route
 * content but keeps the window's scroll position, so navigating from a
 * footer link (page bottom) landed the new page scrolled to its bottom.
 *
 * On every pathname change: scroll to the top, instantly (a smooth
 * animation here would visibly rewind the page the user just left). When
 * the new URL carries a #hash, defer one frame and honor the anchor
 * instead, letting the section's own scroll-margin do the framing.
 */

import { useLayoutEffect } from "react"
import { useLocation } from "react-router-dom"

export function ScrollManager() {
  const { pathname, hash } = useLocation()

  // The browser must not fight us on SPA navigations.
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual"
    }
  }, [])

  // useLayoutEffect + behavior:"instant": the reset lands BEFORE paint, and
  // "instant" is the only value that overrides the global CSS
  // `scroll-behavior: smooth` — "auto" DEFERS to it (the 2026-08-29 v1 of
  // this file got that wrong, so the reset became a smooth scroll that page
  // -mount layout shifts could cancel mid-flight, leaving guides opened at
  // their end).
  useLayoutEffect(() => {
    if (hash) {
      // New page + anchor: wait a frame so the target exists, then jump.
      requestAnimationFrame(() => {
        document.querySelector(hash)?.scrollIntoView()
      })
      return
    }
    window.scrollTo({ top: 0, behavior: "instant" })
  }, [pathname, hash])

  return null
}
